import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { useOutletContext } from "react-router-dom"
import keyBy from "lodash.keyby"

import { VirtualTable } from "@components/Table"
import { useAppContext } from "@components/AppContext"
import { renderTemplate } from "@modules/backstage/activities/templates"

import getUser from "./helpers/getUser"
import useColumnsConfig from "./helpers/useColumnsConfig"
import getExportColumns from "./helpers/getExportColumns"
import useIndexOperationParameters from "./helpers/useIndexOperationParameters"
import ActivitiesTableHeader from "./ActivitiesTableHeader"

const defaultIndexOperation = "activities/IndexActivities"


const ActivitiesTable = ({
  table,
  indexOperation,
  investorAccountId,
  openActivityDrawer
}) => {
  const { isConsoleOrganization } = useAppContext()

  const [ dateRange, updateDateRange ] = useIndexOperationParameters()
  const operationParameters = { ...dateRange, investorAccountId }

  const {
    members,
    accounts,
    projects,
    fundsMap,
    membersMap,
    accountsMap,
    layoutTopOffset
  } = useOutletContext()

  const projectsMap = useMemo(() =>
    keyBy(projects, "id")
  ,[ projects ])

  const isReady =
    isConsoleOrganization ||
    !!projects && !!accounts && !!members

  const context = {
    fundsMap,
    membersMap,
    accountsMap,
    projectsMap,
  }

  table.current.getUser = userId =>
    getUser(userId, membersMap, accountsMap)

  table.current.renderActivity = ({ operationId, userId, parametersJson, resultJson }) =>
    renderTemplate(context, operationId, userId, parametersJson, resultJson)

  const headerRenderTitle = (title, items) => (
    <ActivitiesTableHeader
      title={title}
      items={items}
      updateDateRange={updateDateRange}
    />
  )

  const size = "small"
  const sticky = true
  const columnsConfig = useColumnsConfig(table, openActivityDrawer)
  const exportColumns = getExportColumns(context)

  const extraTopOffset = investorAccountId
    ? 30
    : layoutTopOffset

  const exportFileName = "activities"
  const defaultSortColumn = "createdAt"
  const defaultSortColumnDirection = "descend"

  const tableProps = {
    size,
    table,
    sticky,
    isReady,
    exportColumns,
    columnsConfig,
    indexOperation,
    exportFileName,
    extraTopOffset,
    defaultSortColumn,
    headerRenderTitle,
    operationParameters,
    defaultSortColumnDirection,
  }

  return (
    <VirtualTable {...tableProps} />
  )
}

ActivitiesTable.propTypes = {
  table: PropTypes.shape(),
  indexOperation: PropTypes.string,
  investorAccountId: PropTypes.string,
  openActivityDrawer: PropTypes.func.isRequired,
}

ActivitiesTable.defaultProps = {
  table: undefined,
  indexOperation: defaultIndexOperation,
  investorAccountId: undefined,
}

export default ActivitiesTable
